import { useMutation, useQuery } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../commonApis/commonApis";
import { ApiResponse } from "./loginApis";
const url = process.env.REACT_APP_BASE_URL;

interface CreateConnectionPayload {
  id?: string;
  clientId: string;
  appId: string;
  secretKey: string;
  channelId: string;
}

interface ChannelInstallationPayload {
  id?: string;
  clientId: string;
  appId?: string;
  secretKey?: string;
  channelId: string;
}
interface CreateOrderPayload {
  orderId: string;
}
interface CreateCancelPayload {
  channelId: string;
  requestId: string;
}

export const useIntegrationData = (
  page: number,
  searchFor: string,
  channelType: string,
  value: string | undefined,
) => {
  return useQuery({
    queryKey: ["integrationData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/integrations/channels?page=${page}&searchFor=${searchFor}&channelType=${channelType}&value=${value}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useSalesAllIntegrationData = () => {
  return useQuery({
    queryKey: ["salesIntegrationData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/integrations/channels/all?type=sales`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useSalesChannelByMerchant = (merchantId: string) => {
  return useQuery({
    queryKey: ["salesChannelData", merchantId],
    enabled: !!merchantId,
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/integrations/channels/merchant/${merchantId}?type=sales`,
        "get",
      );

      if (!res || !res.data) {
         throw new Error(res?.message || "Failed to fetch channels");
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchApplicationDetails = (id: string) => {
  return useQuery({
    queryKey: ["applicationDetails", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(
        `${url}/api/channels/applications/${id}?type=LM`,
        "get",
      );

      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchInstalledApplicationDetails = (id: string) => {
  return useQuery({
    queryKey: ["applicationInstalledDetails", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(
        `${url}/api/channels/application/${id}/installed?type=LM`,
        "get",
      );

      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useApplicationIntegrationData = (
  page: number,
  searchFor: string,
) => {
  return useQuery({
    queryKey: ["applicationIntegrationData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/channels/applications?page=${page}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useRequestChannelMutation = (channelId: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/integrations/channels/${channelId}/request`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Integration requested successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Integration request failed:", error.message);
    },
  });
};

export const useCancelChannelMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateCancelPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/integrations/channels/${payload.channelId}/cancel-request/${payload.requestId}`,
        "PATCH",
        {},
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ OTP requested successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ OTP request failed:", error.message);
    },
  });
};

export const useConnectChannelMutation = (route: string) => {
  return useMutation({
    mutationFn: async (payload: CreateConnectionPayload) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/connection${route}`,
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },

    onSuccess: (data) => {
      console.log("✅ Connection requested successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Connection request failed:", error.message);
    },
  });
};

export const useChannelInstallationMutation = (channelId: string) => {
  return useMutation({
    mutationFn: async (payload: ChannelInstallationPayload) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/integrations/channels/${channelId}/install`,
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Connection requested successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Connection request failed:", error.message);
    },
  });
};

export const useChannelConnectionMutation = (
  requestId: string,
  companyName?: string,
  clientId?: string,
) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      let res: ApiResponse;

      const updatedPayload = {
        ...payload,
        clientId,
      };

      if (companyName === "Aramex") {
        // Hit Aramex-specific API
        res = await fetch4(`${url}/api/aramex/connect`, updatedPayload);
      } else if (companyName === "Naqel" || companyName === "Naqel Express") {
        res = await fetch4(`${url}/api/naqel/connect`, updatedPayload);
      } else if (companyName === "ZID") {
        res = await fetch4(`${url}/api/zidapp/auth`, updatedPayload);
        // if(res.success) {
        //     const clientId = updatedPayload.credentials["Client ID"];
        //     const superUserId = await fetch3(`${url}/api/zidapp/${updatedPayload.clientId}/super-user`, "get");

        //     const redirectUri = 
        //        `${url}/api/zidapp/callback/${superUserId}`;
        //      ;

        //     const state = 'admin'
        //     window.location.href =
        //       `https://oauth.zid.sa/oauth/authorize` +
        //       `?client_id=${clientId}` +
        //       `&redirect_uri=${redirectUri}` +
        //       `&response_type=code` +
        //       `&state=${state}`;

        //     return;
        // }       
      } else if (
        companyName?.toLowerCase() === "j&t express" ||
        companyName?.toLowerCase() === "jt express"
      ) {
        res = await fetch4(`${url}/api/jt/connect`, {
          credentials: payload.credentials,
          clientId,
          connectionId: requestId,
        });
      } else {
        res = await fetch4(
          `${url}/api/integrations/connections/${requestId}/connect`,
          payload,
        );
      }
      // Throw error if success false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Connection created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Connection request failed:", error.message);
    },
  });
};

export const useEditChannelMutation = (route: string) => {
  return useMutation({
    mutationFn: async (payload: CreateConnectionPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/connection${route}`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Connection requested successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Connection request failed:", error.message);
    },
  });
};

export const useSallaPendingData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["sallaPendingData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/salla/salla-pending-orders?page=${page}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateSallaOrderMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateOrderPayload) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/salla/create/order`,
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Connection requested successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Connection request failed:", error.message);
    },
  });
};

export const useShopifyPendingData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["sallaPendingData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/shopify/shopify-pending-orders?page=${page}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateShopifyOrderMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateOrderPayload) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/shopify/create/order`,
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Connection requested successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Connection request failed:", error.message);
    },
  });
};

export const useInstallApplicationMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/webhooks/application/${id}/install`,
        payload,
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ Application installed Successfully!:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("❌ Application installed request failed:", error.message);
    },
  });
};

export const useInstallApplicationTestConnectionMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/webhooks/application/${id}/installed/test-connection`,
        payload,
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ Application Test Successfully!:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("❌ Application Test request failed:", error.message);
    },
  });
};

export const useSalesChannelData = () => {
  return useQuery({
    queryKey: ["channelData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/sales-channel/all`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchChannelDetails = (id: string) => {
  return useQuery({
    queryKey: ["channelDetails", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(`${url}/api/integrations/channels/${id}`, "get");

      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

// export const useConnectionDetails = (clientId: string, channelId: string) => {
//   return useQuery({
//     queryKey: ["connectionDetails", clientId, channelId],
//     queryFn: async () => {
//       const response = await fetch3(`${url}/api/sales-channel/application-connection-details?clientId=${clientId}&channelId=${channelId}`,"get");
//       if (!response.ok) {
//         throw new Error("Failed to fetch connection details");
//       }
//       return response.json();
//     },
//     enabled: !!clientId && !!channelId, // fetch only if both values exist
//     staleTime: 5 * 60 * 1000, // 5 minutes cache
//   });
// };

export const useConnectionDetails = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/sales-channel/application-connection`,
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      console.log("resresresresresresresresres", res);

      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ DRS Created successfully:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("❌ DRS create request failed:", error.message);
    },
  });
};

// Get all integration channels without pagination
export const useIntegrationChannelsAll = (type?: string) => {
  return useQuery({
    queryKey: ["integrationChannelsAll", type ?? null],
    queryFn: async () => {
      const query = type ? `?type=${encodeURIComponent(type)}` : "";
      const res = await fetch3(
        `${url}/api/integrations/channels/all${query}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};
 

export const useFetchNaqelCredentials = (companyName?: string, clientId?: string) => {
  return useQuery({
    queryKey: ["naqelCredentials", clientId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/naqel/credentials?clientId=${clientId}`, "get");
      if (!res || !res.data) return null;
      return res.data;
    },
    enabled: (companyName?.toLowerCase() === "naqel express" || companyName?.toLowerCase() === "naqel") && !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};
 

export const useEditNaqelCredentialsMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      credentials: {
        userName: string;
        password: string;
        customerCode: string;
        branchCode: string;
      };
      clientId: string;
      connectionId: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/naqel/connect`, payload);
      if (!res.success) throw res.message || "Failed to update Naqel credentials";
      return res.data;
    },
    onSuccess: () => console.log("✅ Naqel credentials updated"),
    onError: (err: any) => console.error("❌ Naqel edit failed:", err),
  });
};
 
export const useFetchAramexCredentials = (companyName?: string, clientId?: string) => {
  return useQuery({
    queryKey: ["aramexCredentials", clientId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/aramex/credentials?clientId=${clientId}`, "get");
      if (!res || !res.data) return null;
      return res.data;
    },
    enabled: companyName?.toLowerCase() === "aramex" && !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useEditAramexCredentialsMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      credentials: {
        UserName: string;
        Password: string;
        AccountNumber: string,
        AccountPin: string,
        AccountEntity: string,
        AccountCountryCode: string,
        PaymentType: string,
        ProductGroup: string,
        ProductType: string,
      };
      clientId: string;
      connectionId: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/aramex/connect/update`, payload);
      if (!res.success) throw res.message || "Failed to update Aramex credentials";
      return res.data;
    },
    onSuccess: () => console.log("✅ Aramex credentials updated"),
    onError: (err: any) => console.error("❌ Aramex edit failed:", err),
  });
};

// ─── Aramex forward / label / tracking hooks ───────────────────────────────────

export const useForwardOrderToAramex = () => {
  return useMutation({
    mutationFn: async (payload: {
      orderId: string;
      clientId: string;
      noOfPieces?: number;
      channelId?: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/aramex/forward-order`, payload);
      if (!res.success) throw res.message || "Aramex forward order failed";
      return res.data;
    },
    onSuccess: (data) => console.log("✅ Order forwarded to Aramex:", data),
    onError: (error: any) => console.error("❌ Aramex forward order failed:", error),
  });
};

export const usePrintAramexLabel = () => {
  return useMutation({
    mutationFn: async (payload: {
      orderIds: string[];
      clientId: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/aramex/label-print`, payload);
      if (!res.success) throw res.message || "Aramex label print failed";
      return res.data;
    },
    onSuccess: (data) => console.log("✅ Aramex label print result:", data),
    onError: (error: any) => console.error("❌ Aramex label print failed:", error),
  });
};

export const useTrackAramexShipment = () => {
  return useMutation({
    mutationFn: async (payload: {
      shipmentNumbers: string[];
      clientId: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/aramex/tracking`, payload);
      if (!res.success) throw res.message || "Aramex tracking failed";
      return res.data;
    },
    onSuccess: (data) => console.log("✅ Aramex tracking result:", data),
    onError: (error: any) => console.error("❌ Aramex tracking failed:", error),
  });
};


export const useEditZidCredentialsMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      credentials: {
        clientId: string;
        clientSecret: string;
        appId: string;
      };
      clientId: string;
      channelId: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/zidapp/update`, payload);
      if (!res.success) throw res.message || "Failed to update Zid credentials";
      return res.data;
    },
    onSuccess: () => console.log("✅ Zid credentials updated"),
    onError: (err: any) => console.error("❌ Zid edit failed:", err),
  });
};

export const useFetchZidCredentials = (companyName?: string, clientId?: string) => {
  return useQuery({
    queryKey: ["zidCredentials", clientId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/zidapp/credentials?clientId=${clientId}`, "get");

      return res;
    },
    enabled: companyName?.toLowerCase() === "zid" && !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};

const isJtExpress = (companyName?: string) => {
  const normalized = companyName?.toLowerCase().replace(/\s+/g, " ").trim();
  return normalized === "j&t express" || normalized === "jt express";
};

export const useFetchJtCredentials = (companyName?: string, clientId?: string) => {
  return useQuery({
    queryKey: ["jtCredentials", clientId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/jt/credentials?clientId=${clientId}`, "get");
      if (!res || !res.data) return null;
      return res.data;
    },
    enabled: isJtExpress(companyName) && !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useEditJtCredentialsMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      credentials: {
        customerCode: string;
        apiAccount: string;
        privateKey?: string;
      };
      clientId: string;
      connectionId: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/jt/connect`, payload);
      if (!res.success) throw res.message || "Failed to update J&T credentials";
      return res.data;
    },
    onSuccess: () => console.log("✅ J&T credentials updated"),
    onError: (err: any) => console.error("❌ J&T edit failed:", err),
  });
};
